import { Controller } from "@hotwired/stimulus"
import { calculate } from "../modules/salary_lab/calculator_api"
import { formatCurrency, parseNumber } from "../modules/salary_lab/formatter"
import { SmartDebounce, detectEventType } from "../modules/salary_lab/smart_debounce"
import { writeGenericToUrl, readGenericFromUrl } from "../modules/salary_lab/url_state"
import { trackCalculatorUsed } from "../modules/salary_lab/ga4_tracker"

const URL_MAP = { wage_type: "wt", hourly_rate: "rate", hours_per_week: "hrs", weeks_per_year: "weeks", kiwisaver_perc: "ks", include_student_loan: "sl" }
const URL_TYPES = { hourly_rate: "number", hours_per_week: "number", weeks_per_year: "number", kiwisaver_perc: "number", include_student_loan: "boolean" }

export default class extends Controller {
  static targets = [
    "wageChips", "customRateGroup", "hourlyRateInput",
    "hoursInput", "weeksInput", "kiwisaverSelect", "studentLoanCheckbox",
    "resultsPanel", "loadingIndicator", "introContent",
    "heroTakeHome", "heroGross", "heroEffectiveRate", "heroHourly",
    "frequencyBody", "comparisonBody",
    "payeAmount", "accAmount", "kiwisaverAmount", "kiwisaverRow",
    "studentLoanAmount", "studentLoanRow",
    "liveRegion"
  ]

  connect() {
    this._debounce = new SmartDebounce(() => this._performCalculation(), { minDigits: 2 })
    this._wageType = "minimum"
    this._prefillFromUrl()
    this._renderPrerenderedExtras()
  }

  disconnect() {
    this._debounce.cancel()
  }

  selectWageType(event) {
    event.preventDefault()
    this._wageType = event.currentTarget.dataset.wageType

    // Update chip styles
    this.wageChipsTarget.querySelectorAll("button").forEach(btn => {
      if (btn.dataset.wageType === this._wageType) {
        btn.className = "px-3 py-1.5 text-sm font-medium rounded-full transition-colors bg-cyan-700 text-white"
      } else {
        btn.className = "px-3 py-1.5 text-sm font-medium rounded-full transition-colors text-gray-600 hover:text-cyan-700 hover:bg-cyan-50"
      }
    })

    // Show/hide custom rate input
    if (this._wageType === "custom") {
      this.customRateGroupTarget.classList.remove("hidden")
    } else {
      this.customRateGroupTarget.classList.add("hidden")
    }

    this._debounce.schedule("toggle")
  }

  inputChanged(event) {
    const value = this.hasHourlyRateInputTarget ? this.hourlyRateInputTarget.value : undefined
    this._debounce.schedule(detectEventType(event), value)
  }

  optionChanged() {
    this._debounce.schedule("toggle")
  }

  submitForm(event) {
    event.preventDefault()
    this._debounce.schedule("submit")
  }

  // --- Private ---

  async _performCalculation() {
    const params = {
      type: "minimum_wage",
      wage_type: this._wageType,
      hours_per_week: this.hoursInputTarget.value || 40,
      weeks_per_year: this.weeksInputTarget.value || 52,
      kiwisaver_perc: this.kiwisaverSelectTarget.value,
      include_student_loan: this.studentLoanCheckboxTarget.checked
    }

    if (this._wageType === "custom") {
      const rate = parseNumber(this.hourlyRateInputTarget.value)
      if (!rate || rate <= 0) return
      params.hourly_rate = rate
    }

    this._showLoading()

    try {
      const data = await calculate(params)
      this._renderResults(data)
      writeGenericToUrl(params, URL_MAP)
      trackCalculatorUsed("minimum_wage")
    } catch (error) {
      if (this.hasLiveRegionTarget) {
        this.liveRegionTarget.textContent = error.message || "Calculation failed. Please try again."
      }
    }
  }

  _renderResults(data) {
    // Hero section
    this.heroTakeHomeTarget.textContent = formatCurrency(data.net_annual)
    this.heroGrossTarget.textContent = formatCurrency(data.gross_annual)
    this.heroEffectiveRateTarget.textContent = `${data.effective_tax_rate}%`
    this.heroHourlyTarget.textContent = `${formatCurrency(data.hourly_rate, true)}/hr`

    // Deductions
    this.payeAmountTarget.textContent = formatCurrency(data.paye)
    this.accAmountTarget.textContent = formatCurrency(data.acc_levy)

    if (this.hasKiwisaverAmountTarget) {
      this.kiwisaverAmountTarget.textContent = formatCurrency(data.kiwisaver_employee)
      this.kiwisaverRowTarget.classList.toggle("hidden", data.kiwisaver_employee === 0)
    }

    if (this.hasStudentLoanAmountTarget) {
      this.studentLoanAmountTarget.textContent = formatCurrency(data.student_loan_deduction)
      this.studentLoanRowTarget.classList.toggle("hidden", data.student_loan_deduction === 0)
    }

    // Frequency table
    if (data.frequencies) {
      const periods = [
        { key: "annual", label: "Annual" },
        { key: "monthly", label: "Monthly" },
        { key: "fortnightly", label: "Fortnightly" },
        { key: "weekly", label: "Weekly" },
        { key: "hourly", label: "Hourly" }
      ]
      this.frequencyBodyTarget.innerHTML = periods.map(p => {
        const freq = data.frequencies[p.key]
        if (!freq) return ""
        return `<tr>
          <td class="px-6 py-2.5 text-gray-700">${p.label}</td>
          <td class="px-6 py-2.5 text-right tabular-nums font-medium text-gray-900">${formatCurrency(freq.gross)}</td>
          <td class="px-6 py-2.5 text-right tabular-nums font-medium text-cyan-700">${formatCurrency(freq.net)}</td>
        </tr>`
      }).join("")
    }

    // Comparison table (presets)
    if (data.presets) {
      this.comparisonBodyTarget.innerHTML = data.presets.map(p => {
        const isActive = p.wage_type === data.wage_type
        const rowClass = isActive ? "bg-cyan-50" : ""
        return `<tr class="${rowClass}">
          <td class="px-4 py-2.5 text-gray-700">${p.label}<span class="block text-xs text-gray-400">${p.effective_date}</span></td>
          <td class="px-4 py-2.5 text-right tabular-nums font-medium text-gray-900">${formatCurrency(p.hourly_rate, true)}</td>
          <td class="px-4 py-2.5 text-right tabular-nums text-gray-900">${formatCurrency(p.gross_annual)}</td>
          <td class="px-4 py-2.5 text-right tabular-nums font-medium text-cyan-700">${formatCurrency(p.net_annual)}</td>
        </tr>`
      }).join("")
    }

    this._showResults()

    // ARIA
    if (this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent =
        `Take-home pay: ${formatCurrency(data.net_annual)} per year from ${formatCurrency(data.gross_annual)} gross.`
    }
  }

  _prefillFromUrl() {
    const p = readGenericFromUrl(URL_MAP, URL_TYPES)
    if (!p.wage_type) return
    this._wageType = p.wage_type
    this.wageChipsTarget.querySelectorAll("button").forEach(btn => {
      if (btn.dataset.wageType === this._wageType) {
        btn.className = "px-3 py-1.5 text-sm font-medium rounded-full transition-colors bg-cyan-700 text-white"
      } else {
        btn.className = "px-3 py-1.5 text-sm font-medium rounded-full transition-colors text-gray-600 hover:text-cyan-700 hover:bg-cyan-50"
      }
    })
    if (this._wageType === "custom") {
      this.customRateGroupTarget.classList.remove("hidden")
      if (p.hourly_rate) this.hourlyRateInputTarget.value = p.hourly_rate
    }
    if (p.hours_per_week != null) this.hoursInputTarget.value = p.hours_per_week
    if (p.weeks_per_year != null) this.weeksInputTarget.value = p.weeks_per_year
    if (p.kiwisaver_perc != null) this.kiwisaverSelectTarget.value = p.kiwisaver_perc
    if (p.include_student_loan) this.studentLoanCheckboxTarget.checked = true
    if (this.element.dataset.prerendered !== "true") this._performCalculation()
  }

  _renderPrerenderedExtras() {
    if (this.element.dataset.prerendered !== "true") return
    const json = this.element.dataset.prerenderedResults
    if (!json) return
    try {
      const data = JSON.parse(json)
      this._renderResults(data)
    } catch { /* ignore parse errors */ }
  }

  _showLoading() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.remove("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.add("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }

  _showResults() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.add("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.remove("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }
}
